/**
 * Google Analytics component for Next.js applications.
 * https://github.com/vercel/next.js/blob/canary/packages/third-parties/src/types/google.ts
 * This component was pulled into the app, since the thrid-parties package is not supporting setting consent mode.
 */
'use client';

import { useEffect } from 'react';
import Script from 'next/script';

declare global {
  interface Window {
    dataLayer?: Object[];
    [key: string]: any;
  }
}

type GAParams = {
  gaId: string;
  dataLayerName?: string;
  debugMode?: boolean;
  nonce?: string;
};

declare global {
  interface Window {
    dataLayer?: Object[];
  }
}

let currDataLayerName: string | undefined = undefined;

export function GoogleAnalytics(props: GAParams) {
  const { gaId, debugMode, dataLayerName = 'dataLayer', nonce } = props;

  useEffect(() => {
    if (currDataLayerName === undefined) {
      currDataLayerName = dataLayerName;
    }
  }, [dataLayerName]);

  useEffect(() => {
    // performance.mark is being used as a feature use signal. While it is traditionally used for performance
    // benchmarking it is low overhead and thus considered safe to use in production and it is a widely available
    // existing API.
    // The performance measurement will be handled by Chrome Aurora

    performance.mark('mark_feature_usage', {
      detail: {
        feature: 'next-third-parties-ga',
      },
    });
  }, []);

  return (
    <>
      <Script
        id="_next-ga-init"
        dangerouslySetInnerHTML={{
          __html: `
          window['${dataLayerName}'] = window['${dataLayerName}'] || [];
          function gtag(){window['${dataLayerName}'].push(arguments);}
          gtag('js', new Date());

          gtag('config', '${gaId}' ${debugMode ? ",{ 'debug_mode': true }" : ''});
          gtag('consent', 'default', {
            'ad_storage': 'denied',
            'analytics_storage': 'denied',
            'personalization_storage': 'denied',
            'functionality_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied',
          });`,
        }}
        nonce={nonce}
      />
      <Script
        id="_next-ga"
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
        nonce={nonce}
      />
    </>
  );
}

export function sendGAEvent(..._args: Object[]) {
  if (currDataLayerName === undefined) {
    console.warn(`google.tsx: GA has not been initialized`);
    return;
  }

  if (window[currDataLayerName]) {
    window[currDataLayerName].push(arguments);
  } else {
    console.warn(
      `google.tsx: GA dataLayer ${currDataLayerName} does not exist`
    );
  }
}
